/**
 * Split rules (TMMAPP-0112; TMMAPI-0148, MM-D-123; flagged: undrawn, built from the prototype's
 * dialog, card-menu and sortable-list patterns), from the virtual account's page menu for the owner
 * or a Manager. A 600 dialog listing the rules in the order they are tried: the name, a match summary
 * ("{name} · from {contributor} · ≥ {amount}"), the targets as chips ("{envelope or category}
 * {amount or %}"), an enabled switch, a drag handle with Move earlier / later, and a `card` kebab
 * with Edit / Delete. "New rule" opens the editor in its place (one modal at a time, DLG-005). A
 * reorder, an enable switch and a delete are written at once and rolled back on an error. A rule
 * whose author lost authority (TMMAPI-0152 `authorityLost`, MM-D-123) reads "Inactive — {author} no
 * longer manages this virtual account." (a `micro` tag and a `text2` sentence) and stays editable.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { categoriesApi, type Category } from '@/api/categories';
import type { Envelope } from '@/api/envelopes';
import { errorCopy } from '@/api/errorCopy';
import { splitRulesApi, type SplitRule, type SplitRuleInput } from '@/api/splitRules';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { ErrorState } from '@/components/ErrorState';
import { AddIcon } from '@/components/icons';
import { ReorderHandle, SortableCardGrid } from '@/components/SortableCardGrid';
import { bpToPercentText, formatMoneyMinor } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { destructiveItemSx } from '@/shell/objectMenu';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { resolveTokens } from '@/theme/theme';
import { SplitRuleEditorDialog, type SplitRuleMember } from './SplitRuleEditorDialog';

const TITLE_ID = 'split-rules-title';

interface SplitRulesDialogProps {
  virtualAccount: VirtualAccountDetail;
  envelopes: readonly Envelope[];
  onClose: () => void;
  onNotify: (message: string, options?: { severity: 'error' }) => void;
}

/** The people a rule may match as the contributor: the owner and each active member. */
function membersOf(virtualAccount: VirtualAccountDetail): SplitRuleMember[] {
  const seen = new Set<number>([virtualAccount.ownerUserId]);
  const members: SplitRuleMember[] = [
    { userId: virtualAccount.ownerUserId, name: virtualAccount.ownerDisplayName },
  ];
  for (const share of virtualAccount.shares) {
    if (share.status !== 'ACTIVE' || share.userId === null || seen.has(share.userId)) continue;
    seen.add(share.userId);
    members.push({ userId: share.userId, name: share.displayName });
  }
  return members;
}

/** A served rule as the editor's input (the whole value a `PUT` replaces). */
function inputOf(rule: SplitRule, patch: Partial<SplitRuleInput> = {}): SplitRuleInput {
  return {
    name: rule.name,
    enabled: rule.enabled,
    priority: rule.priority,
    ...(rule.matchName !== null ? { matchName: rule.matchName } : {}),
    ...(rule.matchContributorUserId !== null
      ? { matchContributorUserId: rule.matchContributorUserId }
      : {}),
    ...(rule.minAmountMinor !== null ? { minAmountMinor: rule.minAmountMinor } : {}),
    targets: rule.targets.map((target) => ({
      mode: target.mode,
      ...(target.envelopeId !== null ? { envelopeId: target.envelopeId } : {}),
      ...(target.categoryId !== null ? { categoryId: target.categoryId } : {}),
      ...(target.amountMinor !== null ? { amountMinor: target.amountMinor } : {}),
      ...(target.bp !== null ? { bp: target.bp } : {}),
    })),
    ...patch,
  };
}

export function SplitRulesDialog({
  virtualAccount,
  envelopes,
  onClose,
  onNotify,
}: SplitRulesDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const vaId = virtualAccount.id;
  const currency = virtualAccount.currency;
  const members = membersOf(virtualAccount);
  const [rules, setRules] = useState<SplitRule[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<{ rule?: SplitRule } | null>(null);
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [errorTick, setErrorTick] = useState(0);
  const [deleting, setDeleting] = useState<SplitRule | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [list, cats] = await Promise.all([
      splitRulesApi.list(vaId),
      categoriesApi.list(vaId).catch(() => [] as Category[]),
    ]);
    setRules(list);
    setCategories(cats);
    setLoadError(null);
  }, [vaId]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([splitRulesApi.list(vaId), categoriesApi.list(vaId).catch(() => null)])
      .then(([list, cats]) => {
        if (cancelled) return;
        setRules(list);
        if (cats !== null) setCategories(cats);
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error ?? new Error('split-rules'));
      });
    return () => {
      cancelled = true;
    };
  }, [vaId, tick]);

  const envelopeName = (id: number) =>
    envelopes.find((envelope) => envelope.id === id)?.name ??
    virtualAccount.envelopes.find((envelope) => envelope.id === id)?.name ??
    null;
  const categoryName = (id: number) => categories.find((c) => c.id === id)?.name ?? null;

  function summary(rule: SplitRule): string {
    const parts: string[] = [];
    if (rule.matchName !== null) parts.push(rule.matchName);
    if (rule.matchContributorUserId !== null) {
      const name =
        rule.matchContributorName ??
        members.find((m) => m.userId === rule.matchContributorUserId)?.name ??
        null;
      parts.push(t('splitRules.fromContributor', { name: personName(name, t) }));
    }
    if (rule.minAmountMinor !== null) {
      parts.push(
        t('splitRules.atLeast', {
          amount: formatMoneyMinor(rule.minAmountMinor, currency, locale),
        }),
      );
    }
    return parts.join(' · ');
  }

  async function save(input: SplitRuleInput) {
    setBusy(true);
    setEditorError(null);
    try {
      if (editing?.rule === undefined) {
        await splitRulesApi.create(vaId, input);
        onNotify(t('splitRules.created'));
      } else {
        await splitRulesApi.update(vaId, editing.rule.id, input);
        onNotify(t('splitRules.saved'));
      }
      setEditing(null);
      await load().catch(() => setTick((n) => n + 1));
    } catch (err) {
      setEditorError(errorCopy(t, err, 'splitRules.errorGeneric'));
      setErrorTick((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(rule: SplitRule, enabled: boolean) {
    const before = rules;
    setRules((list) => list?.map((r) => (r.id === rule.id ? { ...r, enabled } : r)) ?? list);
    try {
      await splitRulesApi.update(vaId, rule.id, inputOf(rule, { enabled }));
    } catch (err) {
      setRules(before);
      onNotify(errorCopy(t, err, 'splitRules.errorGeneric'), { severity: 'error' });
    }
  }

  async function reorder(ids: Array<string | number>) {
    const before = rules;
    if (before === null) return;
    const next = ids.flatMap((id) => before.filter((rule) => rule.id === Number(id)));
    setRules(next);
    try {
      setRules(
        await splitRulesApi.reorder(
          vaId,
          next.map((rule) => rule.id),
        ),
      );
    } catch (err) {
      setRules(before);
      onNotify(errorCopy(t, err, 'splitRules.errorGeneric'), { severity: 'error' });
      throw err;
    }
  }

  async function confirmDelete() {
    if (deleting === null || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await splitRulesApi.remove(vaId, deleting.id);
      onNotify(t('splitRules.deleted'));
      setDeleting(null);
      await load().catch(() => setTick((n) => n + 1));
    } catch (err) {
      setDeleteError(errorCopy(t, err, 'splitRules.errorGeneric'));
    } finally {
      setDeleteBusy(false);
    }
  }

  if (editing !== null) {
    return (
      <SplitRuleEditorDialog
        {...(editing.rule !== undefined ? { rule: editing.rule } : {})}
        currency={currency}
        envelopes={envelopes}
        categories={categories}
        members={members}
        busy={busy}
        error={editorError}
        errorTick={errorTick}
        onClose={() => setEditing(null)}
        onSubmit={(input) => void save(input)}
      />
    );
  }
  if (deleting !== null) {
    return (
      <ConfirmDialog
        title={t('splitRules.deleteTitle', { name: deleting.name })}
        consequence={t('splitRules.deleteConsequence')}
        confirmLabel={t('splitRules.delete')}
        destructive
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleteBusy) setDeleting(null);
        }}
      />
    );
  }

  const chip = (label: string, key: string) => (
    <Box
      key={key}
      component="span"
      data-split-chip=""
      sx={{
        display: 'inline-flex',
        border: `1px solid ${tokens.line}`,
        borderRadius: '3px',
        p: '2px 7px',
        fontSize: 12,
        color: tokens.text2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );

  const targetLabel = (target: SplitRule['targets'][number]) => {
    const where = [
      target.envelopeId !== null
        ? (target.envelopeName ?? envelopeName(target.envelopeId) ?? t('splitRules.envelope'))
        : null,
      target.categoryId !== null
        ? (categoryName(target.categoryId) ?? t('splitRules.category'))
        : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
    const amount =
      target.mode === 'FIXED'
        ? target.amountMinor !== null
          ? formatMoneyMinor(target.amountMinor, currency, locale)
          : '—'
        : target.bp !== null
          ? bpToPercentText(target.bp, locale)
          : '—';
    return `${where} ${amount}`;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { width: 600, maxWidth: 'calc(100% - 32px)' } } }}
    >
      <DialogTitle id={TITLE_ID}>{t('splitRules.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.text2, mb: 2 }}>
          {t('splitRules.intro', { name: virtualAccount.name })}
        </Typography>
        {/* One plan for bank money (TMMAPP-0173 item 10): a paycheck is divided on its bank account. */}
        <Typography
          variant="body2"
          data-split-rules-bank-help=""
          sx={{ color: tokens.text2, mb: 2 }}
        >
          {t('splitRules.bankHelp')}
        </Typography>
        {rules === null && loadError === null && <Skeleton variant="rounded" height={96} />}
        {rules === null && loadError !== null && (
          <ErrorState
            variant="section"
            error={loadError}
            what={t('errors.what.splitRules')}
            onRetry={() => setTick((n) => n + 1)}
          />
        )}
        {rules !== null && rules.length === 0 && (
          <Typography sx={{ fontSize: 13, color: tokens.text2 }} data-split-empty="">
            {t('splitRules.empty')}
          </Typography>
        )}
        {rules !== null && rules.length > 0 && (
          <SortableCardGrid
            items={rules}
            getId={(rule) => rule.id}
            getName={(rule) => rule.name}
            canReorder={rules.length > 1}
            minCardWidth={600}
            sx={{ gap: 1 }}
            onReorder={reorder}
            renderCard={(rule, handle) => (
              <Paper
                variant="outlined"
                data-split-rule={rule.id}
                sx={{
                  p: '10px 12px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  bgcolor: tokens.surfaceRaised,
                }}
              >
                {handle !== null && <ReorderHandle {...handle} />}
                <Box sx={{ flex: 1, minWidth: 0, pt: '4px' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{rule.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.text2 }}>
                    {summary(rule)}
                  </Typography>
                  {rule.authorityLost && (
                    <Box
                      data-split-rule-inactive=""
                      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '6px' }}
                    >
                      <Typography variant="micro" component="span" sx={{ color: tokens.text2 }}>
                        {t('splitRules.inactive')}
                      </Typography>
                      <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                        {t('splitRules.authorityLost', { name: personName(rule.authorName, t) })}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: '6px' }}>
                    {rule.targets.map((target, index) =>
                      chip(targetLabel(target), `${rule.id}-${index}`),
                    )}
                  </Box>
                </Box>
                <Switch
                  checked={rule.enabled}
                  onChange={(event) => void toggle(rule, event.target.checked)}
                  slotProps={{
                    input: { 'aria-label': t('splitRules.enabledFor', { name: rule.name }) },
                  }}
                />
                <OverflowMenu
                  variant="card"
                  title={rule.name}
                  label={t('splitRules.actions', { name: rule.name })}
                >
                  {(close) => [
                    <MenuItem
                      key="edit"
                      onClick={() => {
                        close();
                        setEditorError(null);
                        setEditing({ rule });
                      }}
                    >
                      {t('splitRules.edit')}
                    </MenuItem>,
                    ...(handle !== null
                      ? [
                          <MenuItem
                            key="earlier"
                            disabled={!handle.canMoveEarlier}
                            onClick={() => {
                              close();
                              handle.onMoveEarlier();
                            }}
                          >
                            {t('reorder.moveEarlier')}
                          </MenuItem>,
                          <MenuItem
                            key="later"
                            disabled={!handle.canMoveLater}
                            onClick={() => {
                              close();
                              handle.onMoveLater();
                            }}
                          >
                            {t('reorder.moveLater')}
                          </MenuItem>,
                        ]
                      : []),
                    <MenuItem
                      key="delete"
                      onClick={() => {
                        close();
                        setDeleteError(null);
                        setDeleting(rule);
                      }}
                      sx={destructiveItemSx(tokens)}
                    >
                      {t('splitRules.delete')}
                    </MenuItem>,
                  ]}
                </OverflowMenu>
              </Paper>
            )}
          />
        )}
        {rules !== null && (
          <>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditorError(null);
                setEditing({});
              }}
              sx={{ mt: 1.5 }}
            >
              {t('splitRules.add')}
            </Button>
            <Typography sx={{ fontSize: 12, color: tokens.text2, mt: 1.5 }} data-rest-note="">
              {t('splitRules.restNote')}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
}
